import React from "react";
import { useTranslation } from "react-i18next";
import { GoalClockIcon, GoalCloseIcon } from "@/apps/goals/assets/icons";
import { GoalHeroStripes } from "@/apps/goals/components/GoalHeroStripes/GoalHeroStripes";
import { useTimeLeft } from "@/apps/goals/hooks/useTimeLeft";
import {
  formatGoalRewardAmount,
  formatTimeLeft,
} from "@/apps/goals/utils/formatTime";
import { useShareGoal } from "@/apps/goals/hooks/useShareGoal";
import Modal from "@/ui/Modal/Modal";
import Typography from "@/ui/Typography/Typography";
import { ShareButton } from "@/apps/goals/components/ShareButton/ShareButton";
import { useServiceT } from "@/utils/hooks/useServiceT";
import cls from "./styles.module.scss";

interface GoalSpinResultModalProps {
  isOpen: boolean;
  rides: number | null;
  rewardAmount: number | null;
  endsAt?: number;
  onConfirm: () => void;
}

export const GoalSpinResultModal = ({
  isOpen,
  rides,
  rewardAmount,
  endsAt,
  onConfirm,
}: GoalSpinResultModalProps) => {
  const { t } = useTranslation();
  const serviceT = useServiceT();
  const timeLeft = useTimeLeft(endsAt ?? Date.now());
  const shareGoal = useShareGoal();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onConfirm}
      hideHeader
      wrapperClassName={cls.modalWrapper}
      contentClassName={cls.modalContent}
    >
      <div className={cls.root}>
        <div className={cls.hero}>
          <GoalHeroStripes variant="modal" />
          <button
            className={cls.closeButton}
            type="button"
            onClick={onConfirm}
            aria-label={t("goals.spinResult.close")}
          >
            <GoalCloseIcon />
          </button>
          <img className={cls.targetIcon} src="/goals/target.png" alt="" />
        </div>
        <div className={cls.timeBadge}>
          <GoalClockIcon />
          <Typography component="span" size="sm" weight="medium" family="Onest">
            {formatTimeLeft(timeLeft)}
          </Typography>
        </div>
        <div className={cls.copy}>
          <Typography component="p" size="md" weight="bold">
            {t("goals.common.goal")}
          </Typography>
          <Typography
            className={cls.goal}
            component="h2"
            size="2xl"
            weight="semibold"
            family="custom"
          >
            {serviceT("goals.spinResult.goal", { count: rides ?? 0 })}
          </Typography>
          <Typography component="p" size="md" weight="semibold">
            {t("goals.spinResult.reward", {
              price: formatGoalRewardAmount(rewardAmount ?? 0),
            })}
          </Typography>
        </div>
        <ShareButton
          onClick={() =>
            shareGoal({
              variant: "active",
              rewardAmount: rewardAmount ?? 0,
              ridesRequired: rides ?? 0,
            })
          }
        />
      </div>
    </Modal>
  );
};
